import { API_AUTH_TYPES, API_ROUTES } from "../../../api/apiConstants";
import ApiRequest from "../../../api/ApiRequest";

/**
 * Ensures a URL has an HTTP or HTTPS scheme, prepending "http://" if neither is present.
 * @param {string} url
 * @returns {string}
 */
export function addHttp(url) {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    url = "http://" + url;
  }
  return url;
}

/**
 * API client used to upload images to DigitalOcean Spaces during editor
 * paste/insert operations. Existing Cloudinary URLs in stored deltas continue
 * to render — the browser fetches them straight from Cloudinary by URL.
 */
const spacesApi = new ApiRequest(
  API_ROUTES.SPACES,
  API_AUTH_TYPES.USER
);

/**
 * Configuration object passed to the `quill-image-uploader` module.
 * The `upload` function uploads a file to Spaces and returns the resulting URL.
 */
export const imageUploaderFunction = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return spacesApi.post({
      endpoint: "upload_image",
      body: formData,
    });
  },
};

/**
 * Quill toolbar configuration defining available formatting controls in the editor.
 * @type {Array}
 */
export const quillEditorToolbar = [
  [{ header: [false, 1, 2, 3, 4, 5, 6] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],

  ["link", "image"],
];

/**
 * Allowed Quill format names. Only formats listed here are preserved when pasting content.
 * @type {string[]}
 */
export const quillEditorFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",

  "indent",
  "link",
  "image",
];

/**
 * Extracts plain text from all Quill Delta ops
 * @param {Array} ops - Quill Delta operations array
 * @returns {string} - Plain text content
 */
export function getPlainTextFromOps(ops) {
  if (!ops || !Array.isArray(ops)) return '';
  return ops
    .map(op => (typeof op.insert === 'string' ? op.insert : ''))
    .join('');
}

/**
 * Counts words across all Quill Delta ops
 * @param {Array} ops - Quill Delta operations array
 * @returns {number} - Word count
 */
export function getWordCountFromOps(ops) {
  const text = getPlainTextFromOps(ops);
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}
